import { UserShortInfo } from '@/shared/types/common';
import {
  ContentMode,
  ILessonFormat,
  ILessonVideo,
  IStudentContentStep,
  LessonCompletionReason,
  LessonTypeEnum,
} from '@/shared/types/lesson';
import type { FileInfo } from '@/shared/types/files';

export interface IEnrollmentResponse {
  enrollments: IEnrollmentResource[];
}

export type EnrollmentTermStatus = 'active' | 'completed';

/** group — ждёт распределения в группу, schedule — группа есть, расписания ещё нет */
export type AwaitingReason = 'group' | 'schedule';

/** Сессия программы без расписания: в урок не войти */
export interface IAwaitingProgrammeSession {
  id: number;
  order: number;
  title: string;
  description: string;
  type: LessonTypeEnum;
  format: ILessonFormat;
  /** Минуты */
  duration: number;
}

export interface IAwaitingProgrammeModule {
  id: number;
  intakeModuleId: number;
  title: string;
  /** Пусто — сессии модуля ещё не добавлены */
  sessions: IAwaitingProgrammeSession[];
}

export interface IAwaitingEnrollmentItem {
  kind: 'awaiting';
  enrollmentId: number;
  reason: AwaitingReason;
  course: {
    id: number;
    type: string | null;
    title: string;
  };
  intake: {
    id: number;
    title: string;
    startDate: string | null;
    endDate: string | null;
  };
  /** null — у потока ещё нет term для студента */
  term: {
    id: number | null;
    number: number | null;
    intakeTermId: number;
    startDate: string | null;
    endDate: string | null;
  } | null;
  groupNumber: number | null;
  studyFormat: string | null;
  /** Пусто — модулей в term ещё нет */
  modules: IAwaitingProgrammeModule[];
}

/** status=active returns both kinds, other filters — terms only */
export type EnrollmentListItem = IEnrollmentTermItem | IAwaitingEnrollmentItem;

export const isAwaitingEnrollmentItem = (
  item: EnrollmentListItem,
): item is IAwaitingEnrollmentItem => item.kind === 'awaiting';

export interface IEnrollmentTermListResponse {
  items: EnrollmentListItem[];
}

export interface IEnrollmentTermItem {
  // A term with schedule: its lessons can be opened
  kind: 'term';
  id: number;
  enrollmentId: number;
  course: {
    id: number;
    type: string;
    title: string;
  };
  intake: {
    id: number;
    title: string;
    startDate: string;
    endDate: string;
  };
  term: {
    id?: number | null;
    number?: number | null;
    intakeTermId: number;
    startDate: string;
    endDate: string;
    isCurrent: boolean;
    hasStarted?: boolean;
  };
  groupNumber: number | null;
  totalModules: number;
  finishedModules: number;
}

export interface IEnrollmentResource {
  id: number;
  course: {
    id: number;
    type: string;
    title: string;
  };
  intake: {
    id: number;
    title: string;
    startDate: string;
    endDate: string;
  };
  terms: [
    {
      id: number;
      number: number;
      startDate: string;
      endDate: string;
      isCurrent: boolean;
    },
    {
      id: number;
      number: number;
      startDate: string;
      endDate: string;
      isCurrent: boolean;
    },
  ];
  groupNumber: number;
  totalModules: number;
  finishedModules: number;
  isCurrent: boolean;
}

// INTAKE
export interface IEnrollmentIntakeResponse {
  course: {
    id: number;
    type: string;
    title: string;
  };
  intake: {
    id: number;
    title: string;
  };
  terms: {
    intakeTermId: number | null;
    number: number;
  }[];
}

// TERMS
export interface IEnrollmentTermsResponse {
  course: {
    id: number;
    title: string;
  };
  intake: {
    id: number;
    title: string;
  };
  term: IEnrollmentTermResource;
}

export interface IEnrollmentTermsRequest {
  enrollmentId: string;
  intakeTermId: string;
}

export interface IEnrollmentTermResource {
  number?: number | null;
  groupNumber?: number | null;
  modules: IEnrollmentModuleResource[];
}

export interface IEnrollmentModuleResource {
  id: number;
  intakeModuleId: number;
  title: string;
  isCompleted: boolean;
  totalDuration: number;
  lessons: IModuleLesson[];
}

// MODULE LESSONS
export interface IModuleLessonsRequest {
  intakeModuleId: string;
  groupNumber: string;
}

export interface IModuleLesson {
  id: number;
  groupLessonId?: number;
  groupExamResubmissionId?: number;
  order: number;
  title: string;
  description: string;
  format?: ILessonFormat;
  duration: number;
  type: LessonTypeEnum;
  isCompleted: boolean;
  completionReason?: LessonCompletionReason | null;
  scheduledAt: string;
  endDate: string;
  isOnlyVideoLesson: boolean;
  attendance?: 0 | 0.5 | 1 | null;
  attendanceValue?: 0 | 0.5 | 1 | null;
  isRequestAllowed?: boolean;
}

export interface IModuleLessonsResource {
  lessons: IModuleLesson[];
  course: {
    id: number;
    title: string;
  };
  intake: {
    id: number;
    title: string;
  };
  group: {
    id: number;
    number: number;
  };
  term: {
    id: number;
    number: number;
    intakeTermId: number;
  };
  module: {
    id: number;
    intakeModuleId: number;
    title: string;
  };
}

// LESSON DETAILS
export interface IEnrollmentLessonRequest {
  enrollmentId: string;
  intakeTermId: string;
  groupLessonId?: string;
  groupExamResubmissionId?: string;
}

export interface IEnrollmentLessonResource {
  lesson: {
    id: number;
    groupLessonId?: number;
    groupExamResubmissionId?: number;
    assignmentId?: number | null;
    title: string;
    description: string | null;
    format?: ILessonFormat;
    video: ILessonVideo | null;
    duration: number;
    order: number;
    type: LessonTypeEnum;
    attachments: FileInfo[];
    contentMode: ContentMode;
    steps: IStudentContentStep[];
    isOnlyVideoLesson: boolean;
    scheduledAt: string;
    endDate: string;
  };
  moduleTeacher: UserShortInfo | null;
  lecturer: UserShortInfo | null;
  lessonNotes: IEnrollmentLessonNote[];
}

export interface IEnrollmentLessonNote {
  id: number;
  author: {
    id: number;
    username: string;
    email: string;
    avatar: string | null;
    role: string;
  };
  message: string;
  createdAt: string;
  attachments: FileInfo[];
}

export interface IEnrollmentLessonNoteRequest {
  groupLessonId?: string;
  groupExamResubmissionId?: string;
  message: string;
  attachments?: File[];
}

export interface IEnrollmentLessonNoteResponse {
  id: number;
}
